import React from "react";
import { Typography, theme, Tag, Flex, Card, Divider, Button, Popconfirm } from "antd";
import {
  PartitionOutlined,
  TagsOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import type { ChartAccountData } from "../types";

const { Title, Text } = Typography;

interface ChartAccountDetailsProps {
  account: ChartAccountData;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
  onAddSubAccount: () => void;
}

const FieldItem = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  const { token } = theme.useToken();
  return (
    <Flex vertical gap={3}>
      <Text
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: token.colorTextSecondary,
        }}
      >
        {label}
      </Text>
      {children}
    </Flex>
  );
};

const SectionCard = ({
  icon,
  title,
  iconBg,
  iconColor,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  iconBg: string;
  iconColor: string;
  children: React.ReactNode;
}) => {
  const { token } = theme.useToken();
  return (
    <Card
      style={{ borderRadius: token.borderRadiusLG, height: "100%" }}
      styles={{ body: { padding: 24 } }}
      hoverable
    >
      <Flex vertical gap={18}>
        <Flex align="center" gap={12}>
          <Flex
            align="center"
            justify="center"
            style={{
              width: 40,
              height: 40,
              borderRadius: token.borderRadiusLG,
              background: iconBg,
              color: iconColor,
              fontSize: 18,
              flexShrink: 0,
            }}
          >
            {icon}
          </Flex>
          <Title level={5} style={{ margin: 0, fontWeight: 800, fontSize: 16 }}>
            {title}
          </Title>
        </Flex>
        <Divider style={{ margin: 0 }} />
        <Flex vertical gap={16}>
          {children}
        </Flex>
      </Flex>
    </Card>
  );
};

const ChartAccountDetails: React.FC<ChartAccountDetailsProps> = ({
  account,
  onEdit,
  onDelete,
  isDeleting,
  onAddSubAccount,
}) => {
  const { token } = theme.useToken();

  return (
    <div style={{ padding: "0 8px 32px" }}>
      <Flex vertical gap={24}>
        {/* ── Profile Header ── */}
        <Flex vertical align="center" gap={10} style={{ textAlign: "center" }}>
          <Title
            level={2}
            style={{
              margin: 0,
              fontWeight: 900,
              fontSize: 26,
              letterSpacing: "-0.02em",
              color: token.colorText,
            }}
          >
            {account.account_name || "Cuenta sin nombre"}
          </Title>

          <Flex align="center" gap={10} wrap justify="center">
            <Tag
              color={
                account.type_account === "Deudora"
                  ? "blue"
                  : account.type_account === "Acreedora"
                    ? "red"
                    : "purple"
              }
              icon={<TagsOutlined />}
              style={{
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                borderRadius: 999,
                margin: 0,
              }}
            >
              Cuenta {account.type_account}
            </Tag>
          </Flex>
        </Flex>

        {/* ── Action Buttons ── */}
        <Flex gap={12} wrap justify="center" style={{ margin: "8px 0" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onAddSubAccount}
            style={{ borderRadius: token.borderRadius }}
          >
            Agregar sub-cuenta
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={onEdit}
            style={{ borderRadius: token.borderRadius }}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar esta cuenta?"
            description="Esta acción marcará la cuenta como eliminada."
            onConfirm={onDelete}
            okText="Sí, eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true, loading: isDeleting }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              style={{ borderRadius: token.borderRadius }}
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Flex>

        {/* ── Info Cards ── */}
        <Flex gap={20} wrap style={{ alignItems: "stretch" }}>
          {/* Detalles de la Cuenta */}
          <div style={{ flex: "1 1 100%" }}>
            <SectionCard
              icon={<PartitionOutlined />}
              title="Estructura y Atributos"
              iconBg={token.colorPrimaryBg}
              iconColor={token.colorPrimary}
            >
              <FieldItem label="Código Agrupador">
                <Text
                  strong
                  copyable
                  style={{
                    fontSize: 16,
                    fontWeight: 900,
                    color: token.colorText,
                  }}
                >
                  {account.grouping_code}
                </Text>
              </FieldItem>

              <FieldItem label="Nivel">
                <Text style={{ color: token.colorText, fontSize: 14 }}>
                  Nivel {account.level}
                </Text>
              </FieldItem>

              <FieldItem label="Tipo de Cuenta">
                <Text style={{ color: token.colorText, fontSize: 14 }}>
                  {account.type_account}
                </Text>
              </FieldItem>
            </SectionCard>
          </div>
        </Flex>
      </Flex>
    </div>
  );
};

export default ChartAccountDetails;
